/**
 * journey-ux/DetailDrawer.jsx — right-side detail drawer (Phase 1)
 *
 * Per DESIGN_DECISIONS.md D-2: clicking a row opens a 320 px right
 * drawer; the grid behind stays interactive. No full-page navigation.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   title: string
 *   eyebrow?: string  (small label above title, e.g. "Patient Case · #247")
 *   children: ReactNode  (body content)
 *   footer?: ReactNode   (action row pinned at bottom)
 *   widthClass?: string  (default 'w-80')
 *   testId?: string
 */
import React, { useEffect } from 'react';
import { X } from 'lucide-react';

export const DetailDrawer = ({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer = null,
  widthClass = 'w-80',
  testId = 'detail-drawer',
}) => {
  // Close on escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <aside
      data-testid={testId}
      className={`${widthClass} border-l border-slate-200 bg-white shrink-0 flex flex-col h-[calc(100vh-180px)]`}
    >
      <header className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          {eyebrow && (
            <div
              className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate"
              data-testid={`${testId}-eyebrow`}
            >
              {eyebrow}
            </div>
          )}
          <div
            className="text-[14px] font-semibold text-slate-900 mt-0.5 truncate"
            data-testid={`${testId}-title`}
          >
            {title}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drawer"
          data-testid={`${testId}-close`}
          className="text-slate-400 hover:text-slate-700 transition-colors p-1 -mr-1"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div
        className="flex-1 overflow-auto p-4 text-[12px] text-slate-700 space-y-3"
        data-testid={`${testId}-body`}
      >
        {children}
      </div>

      {footer && (
        <div
          className="px-4 py-3 border-t border-slate-200 bg-slate-50 shrink-0"
          data-testid={`${testId}-footer`}
        >
          {footer}
        </div>
      )}
    </aside>
  );
};

/** Convenience component for key/value rows inside a drawer. */
export const DrawerField = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-3" data-testid={testId}>
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 font-medium text-right truncate">{value}</span>
  </div>
);

export default DetailDrawer;
